import { TypedDocumentNode, gql } from '@apollo/client';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { SortingOption } from '@sorare/core/src/__generated__/globalTypes';
import { DropdownSelect } from '@sorare/core/src/atoms/inputs/DropdownSelect';
import { extractConnectionData } from '@sorare/core/src/gql/extractData';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useQueryState } from '@sorare/core/src/hooks/useQueryState';

import { MyPage } from '../MyPage';
import { DisplayItems } from '../common/DisplayItems';
import { PrimaryOffer } from '../common/PrimaryOffer';
import { messages } from '../common/messages';
import { DefaultSortType } from '../common/types';
import { useSportSelect } from '../common/useSportSelect';
import type {
  BoughtPrimaryOffersQuery,
  BoughtPrimaryOffersQueryVariables,
} from './__generated__/index.graphql';

const BOUGHT_PRIMARY_OFFERS_QUERY = gql`
  query BoughtPrimaryOffersQuery(
    $cursor: String
    $sortByEndDate: SortingOption
    $sport: [Sport!]
  ) {
    currentUser {
      slug
      boughtPrimaryOffers(
        first: 10
        after: $cursor
        sortByEndDate: $sortByEndDate
        sport: $sport
      ) {
        nodes {
          id
          ...MySorarePrimaryOffer_primaryOffer
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
  ${PrimaryOffer.fragments.primaryOffer}
` as TypedDocumentNode<
  BoughtPrimaryOffersQuery,
  BoughtPrimaryOffersQueryVariables
>;

export const MyPrimaryOffers = () => {
  const { sportInputValue, SportSelect } = useSportSelect();
  const { formatMessage } = useIntl();

  const [{ sortType }, setState] = useQueryState({
    sortType: DefaultSortType.LATEST as DefaultSortType,
  });

  const sortOptions = useMemo(
    () =>
      Object.values(DefaultSortType).map((element: DefaultSortType) => ({
        label: formatMessage(messages[element]),
        value: element,
      })),
    [formatMessage]
  );

  const onSortTypeChange = (value: DefaultSortType) => {
    setState(prevState => ({ ...prevState, sortType: value }));
  };
  const sortByEndDate =
    sortType === DefaultSortType.LATEST
      ? SortingOption.DESC
      : SortingOption.ASC;
  const { loading, data, loadMore } = usePaginatedQuery(
    BOUGHT_PRIMARY_OFFERS_QUERY,
    {
      notifyOnNetworkStatusChange: true,
      connection: 'TokenPrimaryOfferConnection',
      variables: {
        sortByEndDate,
        sport: sportInputValue,
      },
    }
  );

  const { items, count, cursor, hasMore } = extractConnectionData(
    data?.currentUser?.boughtPrimaryOffers,
    primaryOffer => (
      <PrimaryOffer key={primaryOffer.id} primaryOffer={primaryOffer} />
    )
  );

  const displayLoading = !items && loading;

  return (
    <MyPage
      toolbar={
        <>
          <SportSelect />
          <DropdownSelect
            size="medium"
            value={sortType}
            options={sortOptions}
            onChange={value => {
              onSortTypeChange(value);
            }}
          />
        </>
      }
    >
      <DisplayItems
        displayLoading={displayLoading}
        count={count}
        items={items}
        loadMoreSection={{
          hasMore,
          loading,
          loadMore: () => {
            loadMore(false, {
              cursor,
              sortByEndDate,
              sport: sportInputValue,
            });
          },
        }}
      />
    </MyPage>
  );
};
